import axios from 'axios'
import * as Config from "../config"

const state = {
  zones: [],
  companies: [],
  zonesLoaded: false,
  companiesLoaded: false,
  zonesLastUpdated: null,
  companiesLastUpdated: null,
  currentZoneId: null,
  isLoadingZones: false,
  isLoadingCompanies: false
}

const mutations = {
  SET_ZONES(state, zones) {
    state.zones = zones
    state.zonesLoaded = true
    state.zonesLastUpdated = Date.now()
  },
  SET_COMPANIES(state, companies) {
    state.companies = companies
    state.companiesLoaded = true
    state.companiesLastUpdated = Date.now()
  },
  SET_CURRENT_ZONE_ID(state, zoneId) {
    state.currentZoneId = zoneId
    // Si cambia la zona, marcar que las compañías necesitan recargarse
    if (state.currentZoneId !== zoneId) {
      state.companiesLoaded = false
      state.companies = []
    }
  },
  SET_LOADING_ZONES(state, loading) {
    state.isLoadingZones = loading
  },
  SET_LOADING_COMPANIES(state, loading) {
    state.isLoadingCompanies = loading
  },
  CLEAR_COMPANIES(state) {
    state.companies = []
    state.companiesLoaded = false
    state.companiesLastUpdated = null
  }
}

const actions = {
  async loadZones({ commit, state }) {
    // Si ya están cargadas y no han pasado más de 30 minutos, usar caché
    const cacheExpiry = 30 * 60 * 1000 // 30 minutos
    if (state.zonesLoaded && state.zonesLastUpdated && 
        (Date.now() - state.zonesLastUpdated < cacheExpiry)) {
      console.log('Usando zonas desde caché:', state.zones.length)
      return state.zones
    }

    // Si ya está cargando, esperar
    if (state.isLoadingZones) {
      console.log('Ya hay una carga de zonas en proceso, esperando...')
      // Esperar hasta que termine la carga
      while (state.isLoadingZones) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      return state.zones
    }

    console.log('Cargando zonas desde el servidor')
    commit('SET_LOADING_ZONES', true)

    try {
      const response = await axios.get(Config.apiPath + "/zone/all/lite", {
        params: { active: 1 }
      })

      if (response.data.code === 200) {
        const zones = response.data.data || []
        commit('SET_ZONES', zones)
        return zones
      } else {
        console.error("Error al cargar las zonas:", response.data)
        return []
      }
    } catch (error) {
      console.error("Error al cargar las zonas:", error)
      return []
    } finally {
      commit('SET_LOADING_ZONES', false)
    }
  },

  async loadCompanies({ commit, state }, { zoneId, forceReload = false }) {
    // Si no hay zona, no cargar compañías
    if (!zoneId) {
      commit('CLEAR_COMPANIES')
      return []
    }

    // Si cambió la zona, limpiar compañías y forzar recarga
    if (state.currentZoneId !== zoneId) {
      commit('SET_CURRENT_ZONE_ID', zoneId)
      forceReload = true
    }

    // Si ya están cargadas para esta zona y no se fuerza recarga, usar caché
    const cacheExpiry = 30 * 60 * 1000 // 30 minutos
    if (!forceReload && state.companiesLoaded && state.companiesLastUpdated && 
        state.currentZoneId === zoneId &&
        (Date.now() - state.companiesLastUpdated < cacheExpiry)) {
      console.log('Usando compañías desde caché para zona', zoneId, ':', state.companies.length)
      return state.companies
    }

    // Si ya está cargando, esperar
    if (state.isLoadingCompanies) {
      console.log('Ya hay una carga de compañías en proceso, esperando...')
      // Esperar hasta que termine la carga
      while (state.isLoadingCompanies) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
      return state.companies
    }

    console.log('Cargando compañías desde el servidor para zona:', zoneId)
    commit('SET_LOADING_COMPANIES', true)

    try {
      const response = await axios.get(Config.apiPath + "/company/all/lite", {
        params: { id_zone: zoneId }
      })

      if (response.data.code === 200) {
        const companies = response.data.data || []
        commit('SET_COMPANIES', companies)
        return companies
      } else {
        console.error("Error al cargar las compañías:", response.data)
        return []
      }
    } catch (error) {
      console.error("Error al cargar las compañías:", error)
      return []
    } finally {
      commit('SET_LOADING_COMPANIES', false)
    }
  },

  setCurrentZone({ commit }, zoneId) {
    commit('SET_CURRENT_ZONE_ID', zoneId)
  },

  clearCompanies({ commit }) {
    commit('CLEAR_COMPANIES')
  }
}

const getters = {
  getZones: state => state.zones,
  getCompanies: state => state.companies,
  getCurrentZoneId: state => state.currentZoneId,
  isZonesLoaded: state => state.zonesLoaded,
  isCompaniesLoaded: state => state.companiesLoaded,
  isLoadingZones: state => state.isLoadingZones,
  isLoadingCompanies: state => state.isLoadingCompanies
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
